/* Webinars -> Overview.
 *
 * Aggregated client-side from the existing per-campaign endpoints
 * (/funnel, /sessions, /failures?campaign_id=) — there is no single
 * aggregate endpoint and every number here is derivable from those, so no
 * new backend surface was added for it. Every count is an exact server
 * total (count_documents / aggregation), never a sampled list length.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, TestBadge } from "@/components/common";
import { CalendarClock, Video, RefreshCcw } from "lucide-react";
import { StatTile, WebinarStatusBadge, fmtInTz } from "@/components/webinar/webinarShared";

export const OPERATIONAL_STATUSES = ["active", "paused"];
const NON_TERMINAL = ["draft", "active", "paused"];

export const campaignsInScope = (campaigns, { scope, includeTest }) => (campaigns || [])
  .filter((c) => (scope === "all" ? true : OPERATIONAL_STATUSES.includes(c.status)))
  .filter((c) => includeTest || !c.test_mode);

/* Pure aggregation — exported for tests. `details[campaign_id]` holds that
 * campaign's {funnel, sessions, failures} responses (sessions/failures may
 * be absent). */
export const aggregateOverview = ({ campaigns, details, now = new Date() }) => {
  const t = {
    campaigns: campaigns.length,
    activeCampaigns: campaigns.filter((c) => c.status === "active").length,
    upcomingSessions: 0, enrolled: 0, registered: 0, linkPending: 0,
    attended: 0, noShows: 0, ctaEligible: 0, failedAutomations: 0,
  };
  const upcoming = [];
  const perCampaign = {};
  campaigns.forEach((c) => {
    const d = details[c.campaign_id] || {};
    const f = d.funnel || {};
    const linkPending = (f.link_sent_total || 0) + (f.external_pending_total || 0);
    const fl = d.failures;
    const failed = fl
      ? (fl.registration_failures?.total || 0)
        + (fl.message_failures?.totals_by_bucket?.failed || 0)
        + (fl.job_failures?.total || 0)
      : 0;
    t.enrolled += f.enrolled_total || 0;
    t.registered += f.registered_total || 0;
    t.linkPending += linkPending;
    t.attended += f.attended_total || 0;
    t.noShows += f.no_show_total || 0;
    t.ctaEligible += f.cta_eligible_total || 0;
    t.failedAutomations += failed;
    const next = (d.sessions || [])
      .filter((s) => s.status === "scheduled" && new Date(s.scheduled_start) > now)
      .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
    t.upcomingSessions += next.length;
    next.forEach((s) => upcoming.push({ ...s, campaign: c }));
    perCampaign[c.campaign_id] = {
      enrolled: f.enrolled_total || 0, registered: f.registered_total || 0, linkPending,
      attended: f.attended_total || 0, noShows: f.no_show_total || 0,
      ctaEligible: f.cta_eligible_total || 0, failed, nextSession: next[0] || null,
    };
  });
  upcoming.sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  return { totals: t, upcoming, perCampaign };
};

export const WebinarOverview = ({ campaigns, campaignsTotal, onOpenCampaign, refreshKey }) => {
  const [scope, setScope] = useState("operational");
  const [includeTest, setIncludeTest] = useState(true);
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);
  const scoped = useMemo(() => campaignsInScope(campaigns, { scope, includeTest }),
                         [campaigns, scope, includeTest]);

  const load = useCallback(async () => {
    if (!campaigns) return;
    setBusy(true);
    try {
      const entries = await Promise.all(scoped.map(async (c) => {
        const [funnel, sessions, failures] = await Promise.all([
          api.get(`/webinar/campaigns/${c.campaign_id}/funnel`).then((r) => r.data),
          NON_TERMINAL.includes(c.status)
            ? api.get(`/webinar/campaigns/${c.campaign_id}/sessions`).then((r) => r.data.items)
            : Promise.resolve([]),
          api.get(`/webinar/failures?campaign_id=${c.campaign_id}&limit=1`).then((r) => r.data),
        ]);
        return [c.campaign_id, { funnel, sessions, failures }];
      }));
      setDetails(Object.fromEntries(entries));
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  }, [campaigns, scoped]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const agg = useMemo(() => (details ? aggregateOverview({ campaigns: scoped, details }) : null),
                      [details, scoped]);
  const t = agg?.totals;

  return (
    <div className="space-y-4" data-testid="webinar-overview">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="h-9 w-[240px]" data-testid="webinar-overview-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="operational">Active &amp; paused campaigns</SelectItem>
            <SelectItem value="all">All campaigns (incl. draft/completed)</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={includeTest} onCheckedChange={setIncludeTest} data-testid="webinar-overview-include-test" />
          Include TEST campaigns
        </label>
        <Button size="sm" variant="ghost" onClick={load} disabled={busy} data-testid="webinar-overview-refresh">
          <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <span className="text-[11px] text-muted-foreground">
          {scoped.length} campaign{scoped.length === 1 ? "" : "s"} in scope
          {campaignsTotal > (campaigns || []).length ? ` (first ${(campaigns || []).length} of ${campaignsTotal} loaded)` : ""}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        <StatTile label="Active campaigns" value={t?.activeCampaigns} testId="webinar-ov-active" />
        <StatTile label="Upcoming sessions" value={t?.upcomingSessions} testId="webinar-ov-upcoming" />
        <StatTile label="Enrolled" value={t?.enrolled} testId="webinar-ov-enrolled" />
        <StatTile label="Registered" value={t?.registered} testId="webinar-ov-registered" />
        <StatTile label="Link sent / pending" value={t?.linkPending}
                  hint="Shared-link leads not yet confirmed registered" testId="webinar-ov-link-pending" />
        <StatTile label="Attended" value={t?.attended} testId="webinar-ov-attended" />
        <StatTile label="No-shows" value={t?.noShows} testId="webinar-ov-noshows" />
        <StatTile label="CTA-eligible" value={t?.ctaEligible} testId="webinar-ov-cta" />
        <StatTile label="Failed automations" value={t?.failedAutomations}
                  tone={t?.failedAutomations ? "danger" : undefined}
                  hint="Registrations + messages/calls + scheduled jobs" testId="webinar-ov-failed" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Upcoming sessions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!agg ? null : agg.upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center" data-testid="webinar-ov-no-upcoming">
              No upcoming sessions in scope.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="webinar-ov-upcoming-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Starts (campaign timezone)</TableHead><TableHead>Campaign</TableHead>
                    <TableHead>Type</TableHead><TableHead className="text-right">Enrolled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agg.upcoming.slice(0, 12).map((s) => (
                    <TableRow key={s.session_id}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtInTz(s.scheduled_start, s.campaign.timezone)}</TableCell>
                      <TableCell className="text-xs">
                        <button type="button" className="hover:underline text-left"
                                onClick={() => onOpenCampaign(s.campaign.campaign_id, "registrations")}>
                          {s.campaign.name}
                        </button> <TestBadge show={s.campaign.test_mode} />
                      </TableCell>
                      <TableCell className="text-xs capitalize">{s.campaign.campaign_type}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{s.enrolled_total ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {agg.upcoming.length > 12 && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Showing the next 12 of {agg.upcoming.length} upcoming sessions.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Video className="h-4 w-4" /> Campaigns in scope
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!agg ? null : scoped.length === 0 ? (
            <EmptyState icon={Video} title="No campaigns in scope"
                        hint={scope === "operational" ? "No campaign is active or paused. Switch the scope to see drafts and completed campaigns." : "No webinar campaigns yet."}
                        testId="webinar-ov-empty" />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="webinar-ov-campaigns">
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead><TableHead>Status</TableHead>
                    <TableHead>Next session</TableHead>
                    <TableHead className="text-right">Enrolled</TableHead>
                    <TableHead className="text-right">Registered</TableHead>
                    <TableHead className="text-right">Link pending</TableHead>
                    <TableHead className="text-right">Attended</TableHead>
                    <TableHead className="text-right">No-shows</TableHead>
                    <TableHead className="text-right">CTA</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scoped.map((c) => {
                    const p = agg.perCampaign[c.campaign_id] || {};
                    return (
                      <TableRow key={c.campaign_id} data-testid={`webinar-ov-row-${c.campaign_id}`}>
                        <TableCell className="text-xs font-medium">
                          <button type="button" className="hover:underline text-left"
                                  onClick={() => onOpenCampaign(c.campaign_id, "registrations")}>
                            {c.name}
                          </button> <TestBadge show={c.test_mode} />
                        </TableCell>
                        <TableCell><WebinarStatusBadge status={c.status} /></TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {p.nextSession ? fmtInTz(p.nextSession.scheduled_start, c.timezone, { year: false }) : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{p.enrolled}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{p.registered}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{p.linkPending}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{p.attended}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{p.noShows}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{p.ctaEligible}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">
                          {p.failed ? (
                            <button type="button" className="text-[hsl(var(--danger))] hover:underline"
                                    onClick={() => onOpenCampaign(c.campaign_id, "failures")}>{p.failed}</button>
                          ) : 0}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WebinarOverview;
